
import React from 'react';
import { FlowRender } from "../flow-render";
import { RadioButton } from "../prm/radio-button";
import { Text, phoneValidator, dobValidator, ssnValidator, mmddyyyyValidator } from "../prm/text";
import { Name } from "../prm/name";
import { sprintf } from "sprintf-js";
import { GreyBox } from "../../client/funcs/utils";
import {calc_roles_old, is_ira, is_ira_rollover, is_ugma} from "../onboarding-utils";
import { StateOptionMenu } from "../prm/state-menu";
import {OptionMenu} from "../prm/option-menu";
import {get_age} from "../time";
import {GenderOptionMenu} from "../prm/gender-menu";
import {MartialStatusMenu} from "../prm/martial-status-menu";
import {InvestmentAccountType} from "../proto/investment_accounts";

const open_text = 'OK time to open up your account';

const same_address = new RadioButton(
  new Name("same_address"),
  [
    new Name("same_address", "Same Physical Address as first")
  ]);
same_address.do_checkbox = true;

const same_phone = new RadioButton(
  new Name("same_phone"),
  [
    new Name("same_phone", "Same Phone number as first")
  ]);
same_phone.do_checkbox = true;

const same_email = new RadioButton(
  new Name("same_email"),
  [
    new Name("same_email", "Same Email as first")
  ]);
same_email.do_checkbox = true;

const us_citizen = new RadioButton(
  new Name("us_citizen"),
  [
    new Name("citizen","I am a U.S. Citizen"),
    new Name("alien","I am a legal resident"),
  ]);

const ca_checkbox = new RadioButton(
  new Name("ca_checkbox"),
  [
    new Name("ca_tos_pp"),
  ]
);
ca_checkbox.do_checkbox = true;

const fbbs_checkbox = new RadioButton(
  new Name("fbbs_checkbox"),
  [
    new Name("fbbs_tos_pp"),
  ]);
fbbs_checkbox.do_checkbox = true;

const personal_text =
  "All your personal data will be kept secure and confidential.";

const prm_info = [
  {
    id: "first_name",
    label: "First Name",
  },
  {
    id: "last_name",
    label: "Last Name",
  },
  {
    id: "dob",
    label: "Date of Birth",
    fake: {
      path: "date.past",
      arg0: 40,
      arg1: 18,
    },
    validator: dobValidator,
    blurValidator: mmddyyyyValidator,
    hint: "MM/DD/YYYY",
  },
  {
    id: "ss",
    label: "Social Security",
    fake: {
      path: "finance.accountTemplate",
      arg0: "###-##-####",
    },
    validator: ssnValidator,
  },
  {
    id: "phone_number",
    label: "Phone Number",
    fake: {
      path: "phone.phoneNumber",
      arg0: "(###) ###-####",
    },
    validator: phoneValidator,
  },
  {
    id: "email",
    label: "Email Address",
  },
  {
    id: "citizen",
    label: "Citizenship",
    fake: {
      path: "value",
      arg0: "American",
    }
  },
  {
    id: "countryOfBirth",
    label: "Country Of Birth",
    fake: {
      path: "value",
      arg0: "United States",
    }
  },
  {
    id: "address",
    label: "Address",
    fake: {
      path: "address.streetAddress",
    }
  },
  {
    id: "city",
    label: "City",
    fake: {
      path: "address.city",
    }
  },
  {
    id: "state",
    label: "State",
    fake: {
      path: "address.stateAbbr",
    }
  },
  {
    id: "zip",
    label: "Zip",
    fake: {
      path: "address.zipCode",
    }
  },
  {
    id: "country",
    label: "Country",
    fake: {
      path: "value",
      arg0: "United States",
    }
  },
  {
    id: "gender",
    label: "Gender",
  },
  {
    id: "martialStatus",
    label: "Martial Status",
  },
  {
    id: "numDependents",
    label: "Number of Dependents",
    fake: {
      path: "random.num",
      arg0: 0,
      arg1: 3,
    }
  },
  {
    id: "edu1",
    label: "Education Level",
    fake: {
      path: "value",
      arg0: "High School",
    }
  },
];

const TWO_COLS = (elem_0, elem_1) => {
  const container_style = {
    display: "flex",
    marginBottom: "1rem",
  }
  return (
    <div style={container_style}>
      <div style={{ flex: "1"}}>
        { elem_0 }
      </div>
      <div style={{width: "1rem"}}/>
      <div style={{ flex: "1"}}>
        { elem_1 }
      </div>
    </div>
  );
}

const CSZ_COLS = (city, state, zip) => {
  const container_style = {
    display: "flex",
    marginBottom: "1rem",
  }
  return (
    <div style={container_style}>
      <div style={{ flex: "1"}}>
        { city }
      </div>
      <div style={{width: "1rem"}}/>
      <div style={{ flex: "1"}}>
        <div style={{display: "flex"}}>
          <div style={{ flex: "1"}}>
            { state }
          </div>
          <div style={{width: "1rem"}}/>
          <div style={{ flex: "1"}}>
            { zip }
          </div>
        </div>
      </div>
    </div>
  );
}

class PersonalInfo extends FlowRender {

  constructor(index) {
    super(index, [
      ca_checkbox,
      fbbs_checkbox,
      same_address,
      same_phone,
      same_email,
      us_citizen,
    ]);

    this.create_params("role_0");
    this.create_params("role_1");

    same_address.onChangeCallback = this.onCopyInfo(
      same_address, [ 'street', 'city', 'state', 'zip']);
    same_phone.onChangeCallback = this.onCopyInfo(same_phone, ['phone_number']);
    same_email.onChangeCallback = this.onCopyInfo(same_email, ['email']);
    this.url = "personal_info";
  }

  init(componentState, appState, component) {
    super.init(componentState, appState, component);
    if (!component.isBaseAcct()) {
      this.role_0.forEach(prm => {
        prm.setDisabled(componentState, true);
      })
    } else {
      // Just disable the email
      const email_prm = this.role_0.find(prm => prm.name.id === "role_0_email");
      if (email_prm) {
        email_prm.setDisabled(componentState, true);
      }

      const country_prm = this.role_0.find(prm => prm.name.id === "role_0_country");
      if (country_prm) {
        country_prm.setValue(componentState, "U.S.A.");
      }
    }
  };

  canNavigateBack = (component) => {
    const onboarding = component.getOnboardingData();
    if (onboarding && onboarding.first_page === "personal_info") {
      return false;
    }
    return true;
  }

  get_param = (prm_suffix, index) => {
    const prm_id = sprintf("role_%d_%s", index, prm_suffix);
    return this.prmList.find(prm => prm.name.id === prm_id);
  }

  create_params = (prefix) => {

    const fake_first_name = {
      path: "name.firstName",
    };
    const fake_last_name = {
      path: "name.lastName",
    };
    const fake_email = {
      path: "internet.email",
    }

    this[prefix] = [];

    prm_info.forEach(({id, label, fake, validator, blurValidator, hint}) => {
      let local_fake = fake;
      if (prefix === "role_1") {
        if (id === 'first_name') {
          local_fake = fake_first_name;
        } else if (id === 'last_name') {
          local_fake = fake_last_name;
        } else if (id === 'email') {
          local_fake = fake_email;
        }
      }

      const new_id = prefix + "_" + id;
      let prm;

      if (id === "state") {
        const menu = new StateOptionMenu(new Name(new_id, label));
        menu.fake = 1;
        menu.onChangeCallback = this.onChange;
        prm = menu;
      } else if (id === "gender") {
        const menu = new GenderOptionMenu(new Name(new_id, label));
        menu.fake = 1;
        menu.onChangeCallback = this.onChange;
        prm = menu;
      } else if (id === "martialStatus") {
        const menu = new MartialStatusMenu(new Name(new_id, label));
        menu.fake = 1;
        menu.onChangeCallback = this.onChange;
        prm = menu;
      } else {
        const text = new Text(
          new Name(new_id, label), null, local_fake);
        if (validator) {
          text.setValidator(validator);
        }
        if (blurValidator) {
          text.setBlurValidator(blurValidator);
        }
        if (hint) {
          text.hint = hint;
        }
        text.onAction = this.onTextEdit;
        prm = text;
      }

      this.prmList.push(prm);
      this[prefix].push(prm);
    });
  };

  setChildAgeError = (component, prm, curr_age, ugma_age, new_state) => {
    const highlight_rects = [ prm.getHighlightRect() ];

    Object.assign(new_state, { highlight_rects });
    new_state.role_1_curr_age = curr_age;
    new_state.role_1_ugma_age = ugma_age;
  }

  check_ugma_dob = (component, state) => {
    const onboarding = component.getOnboardingData();
    const _is_ugma = is_ugma(onboarding);
    if (!_is_ugma) {
      return {
        highlight_rects: [],
        role_1_curr_age: "",
        role_1_ugma_age: "",
      }
    }

    const ugma_age = parseInt(onboarding.child_age_menu, 10);
    const prm = this.prmList.find(prm => prm.name.id === "role_1_dob");
    const dob = prm.getValue(state);
    const curr_age = get_age(dob);

    if (curr_age >= ugma_age) {
      return {
        highlight_rects: [ prm.getHighlightRect() ],
        role_1_curr_age: curr_age,
        role_1_ugma_age: ugma_age,
      }
    }

    return {
      highlight_rects: [],
      role_1_curr_age: "",
      role_1_ugma_age: "",
    };
  }

  /** Called after a text entry is finished editing. */
  onTextEdit = (component, prm) => {
    const newState = Object.assign({}, component.state);
    this.onChange(component, newState);

    if (prm.name.id === "role_1_dob") {
      const result = this.check_ugma_dob(component, newState);
      if (result) {
        Object.assign(newState, result);
      }
    }
    component.setState(newState);
  };

  onFinish = (component, new_state) => {

    if (component.state.dirty && component.isBaseAcct()) {
      // Start the Dwolla customer creation process.
      component.maybeCreateDwollaCustomer();
    }

    const onboarding = component.getOnboardingData();
    // Verify that we need to save the source-of-funds data.
    let new_onboarding_data;
    if (is_ira_rollover(onboarding)) {
      console.log("setSourceOfFunds rollover");
      //new_onboarding_data = set_source_of_funds("other", "Rollover");
    } else if (is_ira(onboarding)) {
      console.log("setSourceOfFunds salary");
      //new_onboarding_data = set_source_of_funds("salary");
    }

    if (new_onboarding_data) {
      component.writeIfDirty(new_onboarding_data);
    }

    // Set the user's name
    const up_case = str => {
      return str[0].toUpperCase() + str.substring(1).toLowerCase();
    }

    const first_prm = this.role_0.find(prm => prm.name.id === "role_0_first_name");
    const last_prm = this.role_0.find(prm => prm.name.id === "role_0_last_name");
    let first_name = first_prm.getValue(new_state).trim();
    if (first_name.includes(" ")) {
      first_name = first_name.split(" ")[0];
    }
    first_name = up_case(first_name);
    const last_name = up_case(last_prm.getValue(new_state).trim());
    component.setUserName(first_name, last_name);
  }

  onCopyInfo = (prm, prms_to_copy) => (component, newState) => {
    if (prm.isClicked(newState)) {
      this.doAddressCopy(newState, prms_to_copy);
    } else {
      this.doAddressEnable(newState, prms_to_copy);
    }
  };

  doAddressEnable = (newState, prms_to_copy) => {
    prms_to_copy.forEach(prm_id => {
      const prm = this.findPrm("role_1_" + prm_id);
      if (!prm) {
        return;
      }
      prm.setDisabled(newState, false);
    })
  };

  doAddressCopy = (newState, prms_to_copy) => {
    newState.dirty = true;

    prms_to_copy.forEach(prm => {
      const from_id = "role_0_" + prm;
      const to_id = "role_1_" + prm;
      const from_prm = this.findPrm(from_id);
      const to_prm = this.findPrm(to_id);
      if (!from_prm || !to_prm) {
        // Iteration break
        return;
      }
      if (to_prm.type === Text.type) {
        to_prm.setValue(newState, from_prm.getValue(newState));
      } else if (to_prm.type === OptionMenu.type) {
        to_prm.setValue(newState, from_prm.getValue(newState).id);
      }
      to_prm.setDisabled(newState, true);
    });
  }

  canNavigateNext(component) {
    const num_roles = calc_roles_old(component.getOnboardingData(component.props)).length;
    let all_prm = [];
    if (num_roles === 1) {
      all_prm = [...this.role_0];
    } else if (num_roles === 2) {
      all_prm = this.role_0.concat(this.role_1);
    }
    all_prm = all_prm.filter(prm => prm.type !== OptionMenu.type);
    all_prm.push(us_citizen);

    const do_cde_agreements = component.props.wlf.apps && component.props.wlf.apps.cd_explorer;

    if (component.isBaseAcct() && do_cde_agreements) {
      all_prm.push(ca_checkbox);
      all_prm.push(fbbs_checkbox);
    }

    const highlight_rects = [];
    all_prm.forEach(prm => prm.isFilledElseHighlight(component.state, highlight_rects));

    if (highlight_rects.length > 0) {
      component.setState({ highlight_rects });
      return null;
    }

    const result = this.check_ugma_dob(component, component.state);
    if (result.highlight_rects.length > 0) {
      component.setState(result);
      return null;
    }

    component.writeIfDirty({
      account_type: InvestmentAccountType.individual.id,
    })

    return component.getRootUrl() + "/income_net_worth";
  };

  renderColumn = (component, index, role) => {
    const prm_group = sprintf("role_%d", index);
    const prm_list = this[prm_group];
    const span_style = {
      marginRight: "1rem",
    };
    const address_text = "Physical Address";
    const is_first = index === 0;
    const all_prms = prm_list.map(prm => {
      const container_style = {
        marginTop: "1rem",
      };
      const label_style = {
        paddingLeft: "1rem",
      };
      const prefix_elems = {
        street: [
          (<span style={span_style}>{address_text}</span>),
          same_address.renderCheckboxLabel(0, component, {}, label_style),
        ],
        phone_number: [
          (<span style={span_style}>{' '}</span>),
          same_phone.renderCheckboxLabel(0, component, {}, label_style),
        ],
        email: [
          (<span style={span_style}>{' '}</span>),
          same_email.renderCheckboxLabel(0, component, {}, label_style),
        ],
      };
      let prefix = null;
      const prefix_index = is_first ? 0 : 1;
      Object.keys(prefix_elems).forEach(prop => {
        if (prm.name.id.endsWith(prop)) {
          prefix = prefix_elems[prop];
        }
      });
      const input_style = {
        width: "100%",
      }

      let prm_div;
      if (prm instanceof Text) {
        prm_div = prm.renderInputDiv(component, input_style, prm.name.label);
      } else if (prm instanceof OptionMenu) {
        prm_div = prm.renderMenuDiv(component, input_style, prm.name.label);
      }
      const is_dob = prm.name.id.endsWith("_dob");
      let err_elem;
      if (is_dob && component.state.role_1_curr_age && component.state.role_1_ugma_age) {
        const text = sprintf(
          "Child is %d and too old. This account transfers to the child at %d.",
          component.state.role_1_curr_age,
          component.state.role_1_ugma_age);
        const style = {
          color: "red",
          paddingTop: "0.5rem",
        }
        if (index === 0) {
          style.visibility = "hidden";
        }
        err_elem = (
          <div style={style}>
            { text }
          </div>
        )
      }
      return (
        <div key={prm.name.id} align={"left"} style={container_style}>
          { prefix && prefix[prefix_index] }
          { prm_div }
          { err_elem }
        </div>
      );
    });
    const column_style = {
      marginTop: "2rem",
    };
    return (
      <div style={column_style}>
        { role && <div align={"center"}>{role}</div> }
        <GreyBox>
          { all_prms }
        </GreyBox>
      </div>
    )
  };


  renderCDEAttestation = component => {
    const checkbox_style = {
      transform: "scale(1.2) translate(0, 0.2rem)",
    }
    const cde_att_0 = "By using the CDexplorer platform you consent to the exchange of "
      + "data between First Bankers' Banc Securities, Inc. and CleverAlpha Asset "
      + "Management, LLC. This exchange of data is to best ensure that the software "
      + "operates properly. Your data will not be rented, sold or utilized for any "
      + "other purpose.";

    return (
      <div style={{display: "flex"}}>
        <div>
          { ca_checkbox.renderCheckbox(0, component, checkbox_style) }
        </div>
        <div style={{display: "inline-block", marginLeft: "2rem"}}>
          <div>{cde_att_0}</div>
        </div>
      </div>
    )
  }

  renderFBBSAttestation = component => {

    const link = (url, text) => (<a href={url} target="_blank" rel="noopener noreferrer">{text}</a>);
    const fbbs_tos = "https://firstbankersbanc.com/terms-of-use/";
    const fbbs_pp = " https://firstbankersbanc.com/privacy-policy/";

    const checkbox_style = {
      transform: "scale(1.2) translate(0, 0.2rem)",
    }
    const container_style = {
      display: "flex",
      paddingTop: "1rem",
    }
    return (
      <div style={container_style}>
        <div>
          { fbbs_checkbox.renderCheckbox(0, component, checkbox_style) }
        </div>
        <div style={{paddingLeft: "2rem"}}>
          <span>
            { "By checking this box, you agree to First Bankers' Banc Securities, Inc " }
            { link(fbbs_tos, "Terms of Use") }
            { ' and ' }
            { link(fbbs_pp, "Privacy Policy")}
            { '.' }
          </span>
        </div>
      </div>
    );
  }

  renderAgreement = component => {
    const text = "Agreement is required to open an account."
    const container_style = {
      paddingTop: "1rem",
      paddingLeft: "3rem",
    }
    return (
      <div style={container_style}>
        {text}
      </div>
    )
  }

  renderCitizen = component => {

    const cb_style = {
      float: "none",
      marginRight: "1rem",
    }
    const render_ck_label = (index) => {
      return (
        <div>
          { us_citizen.renderCheckbox(index, component, cb_style) }
          { us_citizen.buttonList[index].label }
        </div>
      )
    }

    const container_style = {
      marginTop: "2rem",
    }

    const item_style = {
      width: "100%",
      display: "flex",
    }
    const item = (
      <div style={item_style}>
        <div style={{flex: "1"}}>
          { render_ck_label(0) }
        </div>
        <div style={{flex: "1"}}>
          { render_ck_label(1) }
        </div>
      </div>
    )
    return (
      <div style={container_style}>
        { TWO_COLS(item, null) }
      </div>
    )
  }

  renderRole = (component, index) => {
    const container_style = {
      width: "75%",
      margin: "auto",
      marginTop: "2rem",
    }
    const first_name = this.get_param("first_name", index);
    const last_name = this.get_param("last_name", index);
    const ssn = this.get_param("ss", index);
    const dob = this.get_param("dob", index);
    const email = this.get_param("email", index);
    const phone = this.get_param("phone_number", index);
    const citizen = this.get_param("citizen", index);
    const cob = this.get_param("countryOfBirth", index);
    const address = this.get_param("address", index);
    const city = this.get_param("city", index);
    const state = this.get_param("state", index);
    const zip = this.get_param("zip", index);
    const gender = this.get_param("gender", index);
    const martialStatus = this.get_param("martialStatus", index);
    const numDepend = this.get_param("numDependents", index);
    const edu1 = this.get_param("edu1", index);

    const input_style = {
      width: "100%",
    }

    const hr_style = {
      backgroundColor: "black",
      margin: "1rem",
      marginTop: "2rem",
    }

    const do_cde_agreements = component.props.wlf.apps && component.props.wlf.apps.cd_explorer;

    return (
      <div style={container_style} key={index}>
        <GreyBox>
          { TWO_COLS(
            first_name.renderInputDiv(component, input_style, "First Name"),
            last_name.renderInputDiv(component, input_style, "Last Name"))}
          { TWO_COLS(
            ssn.renderInputDiv(component, input_style, "Social Security Number"),
            dob.renderInputDiv(component, input_style, "Date of Birth"))}
          { TWO_COLS(
            email.renderInputDiv(component, input_style, "Email"),
            phone.renderInputDiv(component, input_style, "Phone Number"))}
          { TWO_COLS(
            citizen.renderInputDiv(component, input_style, "Citizenship"),
            cob.renderInputDiv(component, input_style, "Country of Birth"))}
          { address.renderInputDiv(component, input_style, "Address") }
          <div style={{ height: "1rem"}}/>
          { CSZ_COLS(
            city.renderInputDiv(component, input_style, "City"),
            state.renderMenuDiv(component, input_style, "State"),
            zip.renderInputDiv(component, input_style, "Zip"))}
          { TWO_COLS(
            martialStatus.renderMenuDiv(component, input_style, "Martial Status"),
            gender.renderMenuDiv(component, input_style, "Gender"))}
          { TWO_COLS(
            numDepend.renderInputDiv(component, input_style, "Number of Dependents"),
            edu1.renderInputDiv(component, input_style, "Education Level")) }
          { this.renderCitizen(component) }
          { do_cde_agreements && <div>
            <div>
              {component.isBaseAcct() && <hr style={hr_style}/>}
            </div>
            {component.isBaseAcct() && this.renderCDEAttestation(component)}
            {component.isBaseAcct() && this.renderFBBSAttestation(component)}
            {component.isBaseAcct() && this.renderAgreement(component)}
          </div>}
        </GreyBox>
      </div>
    )
  }

  renderHeader = (component) => {
    return (
      <div>
        { this.renderTitle(component, open_text, "75%") }
        { this.renderProgress(component, "75%") }
        { this.renderSingleColumn( (
          <div>
            <div>{personal_text}</div>
          </div>
        ), "75%") }
      </div>
    )
  };

  render = component => {
    const roles = calc_roles_old(component.getOnboardingData(component.props));
    const rows = roles.map((role, index) => {
      return this.renderRole(component, index);
    });

    const section_style = {
      paddingTop: "0",
      paddingBottom: "0",
    }
    return (
      <div>
        { this.renderHighlightRects(component) }
        { this.renderHeader(component) }
        <section className="section" style={section_style}>
          { rows }
        </section>
        { this.renderNextButtonRow(component, this.canNavigateBack(component), "Agree & Continue") }
      </div>
    )
  }
}

export { PersonalInfo };
